export const parseTimeSlot = (
  timeSlot: string
): {
  startTime: { hours: number; minutes: number };
  endTime: { hours: number; minutes: number };
} => {
  // Handle time range format: "7:30PM - 8:30PM", "11AM - 12PM", or "19:30 - 20:30"
  // Updated regex to handle formats with or without minutes
  const timeRangeMatch = timeSlot.match(
    /(\d{1,2})(?::(\d{2}))?(AM|PM)?\s*-\s*(\d{1,2})(?::(\d{2}))?(AM|PM)?/i
  );

  if (timeRangeMatch) {
    const [
      ,
      startHoursStr,
      startMinutesStr,
      startPeriod,
      endHoursStr,
      endMinutesStr,
      endPeriod
    ] = timeRangeMatch;

    let startHours = parseInt(startHoursStr);
    const startMinutes = startMinutesStr ? parseInt(startMinutesStr) : 0;

    if (startPeriod) {
      if (startPeriod.toUpperCase() === 'PM' && startHours !== 12) {
        startHours += 12;
      } else if (startPeriod.toUpperCase() === 'AM' && startHours === 12) {
        startHours = 0;
      }
    }

    let endHours = parseInt(endHoursStr);
    const endMinutes = endMinutesStr ? parseInt(endMinutesStr) : 0;

    if (endPeriod) {
      if (endPeriod.toUpperCase() === 'PM' && endHours !== 12) {
        endHours += 12;
      } else if (endPeriod.toUpperCase() === 'AM' && endHours === 12) {
        endHours = 0;
      }
    } else if (startPeriod) {
      // If end time doesn't have period but start time does, assume same period
      if (startPeriod.toUpperCase() === 'PM' && endHours !== 12) {
        endHours += 12;
      } else if (startPeriod.toUpperCase() === 'AM' && endHours === 12) {
        endHours = 0;
      }
    }

    return {
      startTime: { hours: startHours, minutes: startMinutes },
      endTime: { hours: endHours, minutes: endMinutes }
    };
  }

  // Handle single time format: "7:30PM", "11AM", or "19:30"
  const singleTimeMatch = timeSlot.match(/(\d{1,2})(?::(\d{2}))?(AM|PM)?/i);
  if (singleTimeMatch) {
    const [, hoursStr, minutesStr, period] = singleTimeMatch;
    let hours = parseInt(hoursStr);
    const minutes = minutesStr ? parseInt(minutesStr) : 0;

    if (period) {
      if (period.toUpperCase() === 'PM' && hours !== 12) {
        hours += 12;
      } else if (period.toUpperCase() === 'AM' && hours === 12) {
        hours = 0;
      }
    }

    // For single time, assume 1 hour duration
    const endHours = (hours + 1) % 24;

    return {
      startTime: { hours, minutes },
      endTime: { hours: endHours, minutes }
    };
  }

  // Default fallback
  return {
    startTime: { hours: 0, minutes: 0 },
    endTime: { hours: 1, minutes: 0 }
  };
};

/**
 * Converts a Date object representing Asia/Kolkata (IST) time to UTC
 * Asia/Kolkata is UTC+5:30, so we subtract 5:30 hours to get UTC
 *
 * @param date - Date object representing time in IST timezone
 * @returns Date object representing the equivalent UTC time
 */
export const convertISTToUTC = (date: Date): Date => {
  const istYear = date.getFullYear();
  const istMonth = date.getMonth();
  const istDay = date.getDate();
  const istHours = date.getHours();
  const istMinutes = date.getMinutes();
  const istSeconds = date.getSeconds();

  const utcDate = new Date(
    Date.UTC(istYear, istMonth, istDay, istHours, istMinutes, istSeconds)
  );

  const istOffsetMs = 5.5 * 60 * 60 * 1000;
  return new Date(utcDate.getTime() - istOffsetMs);
};
